<template>
  <div class="backdrop" :class="{ '-show': show }" @click="close"></div>
  <div class="filePicker" :class="{ '-show': show, '-isAdmin': isAdmin }">
    <button type="button" class="btn btn-primary" @click="camera">
      <FontAwesomeIcon icon="fa-light fa-camera" class="me-2" />
      {{ $t("filePicker.camera") }}
    </button>
    <button type="button" class="btn btn-primary" @click="pictures">
      <FontAwesomeIcon icon="fa-light fa-images" class="me-2" />
      {{ $t("filePicker.pictures") }}
    </button>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";

export default {
  name: "MobileFilePickerComponent",
  emits: ["photo", "close"],
  components: { FontAwesomeIcon },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(value) {
        if (value) {
          document.addEventListener("backbutton", this.handleBackButton, { capture: true });
        } else {
          document.removeEventListener("backbutton", this.handleBackButton, { capture: true });
        }
      },
    },
  },
  beforeUnmount() {
    document.removeEventListener("backbutton", this.handleBackButton, { capture: true });
  },
  computed: {
    isAdmin() {
      return this.$route.matched.find((item) => item.path === "/admin");
    },
  },
  methods: {
    handleBackButton(event) {
      event.stopImmediatePropagation();
      this.close();
    },

    async camera() {
      const hasPermission = await this.checkAndRequestPermission("camera");
      if (!hasPermission) return;

      try {
        const photo = await this.getPhoto(CameraSource.Camera);
        this.$emit("photo", photo);
      } catch (e) {
        notificationManager.showNotification("error", i18n.global.t("filePicker.error"));
      }
    },

    async pictures() {
      const hasPermission = await this.checkAndRequestPermission("photos");
      if (!hasPermission) return;

      try {
        const photo = await this.getPhoto(CameraSource.Photos);
        this.$emit("photo", photo);
      } catch (e) {
        notificationManager.showNotification("error", i18n.global.t("filePicker.error"));
      }
    },

    async checkAndRequestPermission(permission) {
      const status = await Camera.checkPermissions();
      if (status[permission] === "granted") {
        return true;
      }

      if (status[permission] === "denied") {
        notificationManager.showNotification("error", i18n.global.t(`filePicker.permission_denied_${permission}`));
        return false;
      }

      const result = await Camera.requestPermissions({ permissions: [permission] });
      if (result[permission] === "granted") {
        return true;
      }

      notificationManager.showNotification("error", i18n.global.t(`filePicker.permission_denied_${permission}`));
      return false;
    },

    async getPhoto(source) {
      return await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: source,
        width: 1920,
        height: 1080,
      });
    },

    close() {
      this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
.filePicker {
  position: fixed;
  bottom: 60px;
  left: 0;
  right: 0;
  background: $white;
  z-index: 1031;
  padding: 15px;
  box-shadow: rgba(0, 0, 0, 0.15) 0px 5px 15px 0px;
  transform: translateY(100%);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease-out;

  &.-isAdmin {
    bottom: var(--safe-area-inset-bottom, 0px);
  }

  &.-show {
    opacity: 1;
    transform: translateY(0);
    visibility: visible;
  }

  button {
    width: 100%;
  }

  button + button {
    margin-top: 10px;
  }
}

.backdrop {
  background: rgba(0, 0, 0, 0.3);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1030;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease-out;

  &.-show {
    opacity: 1;
    visibility: visible;
  }
}
</style>
